/** Format/environment checks only. Provider authentication and storage remain server-owned. */
export const PADDLE_CREDENTIAL_VERIFICATION_ERROR =
  'Could not verify the Paddle API key. Check the key and selected environment, then try again.';

export function isPaddleWebhookSecret(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length <= 1024 && /^pdl_ntfset_\S+$/.test(value.trim());
}

export function validatePaddleConnectionInput(input: unknown): string | null {
  if (!input || typeof input !== 'object') return 'Enter Paddle credentials and choose an environment.';
  const { apiKey, clientToken, webhookSecret, environment } = input as Record<string, unknown>;
  if (environment !== 'sandbox' && environment !== 'production') return 'Choose Sandbox or Production.';
  if (typeof apiKey !== 'string' || !/^pdl_(live|sdbx)_apikey_[a-z\d]{26}_[a-zA-Z\d]{22}_[a-zA-Z\d]{3}$/.test(apiKey.trim())) {
    // Paddle reveals a key once; a masked or listed value is the usual wrong paste.
    const [label, prefix] = environment === 'sandbox' ? ['Sandbox', 'pdl_sdbx_apikey_'] : ['Production', 'pdl_live_apikey_'];
    return `This is not a complete Paddle API key. A ${label} key starts with ${prefix} and is 69 characters long. `
      + 'Paddle shows a key only once, when it is created. If you did not save it, create a new key in '
      + 'Developer tools → Authentication and paste it here.';
  }
  const keyPrefix = environment === 'sandbox' ? 'pdl_sdbx_' : 'pdl_live_';
  const tokenPrefix = environment === 'sandbox' ? 'test_' : 'live_';
  if (!apiKey.trim().startsWith(keyPrefix)) return `Use the API key for the selected ${environment} environment.`;
  if (typeof clientToken !== 'string' || clientToken.trim().length > 1024 ||
      !new RegExp(`^${tokenPrefix}\\S+$`).test(clientToken.trim())) {
    return `Use the client-side token for the selected ${environment} environment (${tokenPrefix}…).`;
  }
  if (webhookSecret !== undefined && (typeof webhookSecret !== 'string' ||
      (webhookSecret.trim() !== '' && !isPaddleWebhookSecret(webhookSecret)))) {
    return 'Enter the webhook signing secret from your Paddle notification destination.';
  }
  return null;
}
